import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Service } from "src/app/core/jsonapi/service";
import { environment } from "src/environment/environment";
import { Call, CallTimelineEvent } from "../_types/call";

@Injectable({
    providedIn: 'root',
})
export class CallService extends Service<Call> {
    public override type = 'call';
    public override url = 'Communications/calls';

    /** Etiquetas y severidad (p-tag) por estado de llamada. */
    getStatuses() {
        return [
            { label: 'Atendida', value: 'Answered', severity: 'success' },
            { label: 'Atendida (IVR)', value: 'AnsweredIvr', severity: 'info' },
            { label: 'No contestada', value: 'NoAnswer', severity: 'danger' },
            { label: 'Comunica', value: 'Busy', severity: 'warn' },
            { label: 'Abandonada en cola', value: 'AbandonedInQueue', severity: 'danger' },
            { label: 'Desviada', value: 'Diverted', severity: 'info' },
        ];
    }

    getStatusLabel(value?: string): string {
        return this.getStatuses().find(s => s.value === value)?.label ?? (value ?? '');
    }

    getStatusSeverity(value?: string): any {
        return this.getStatuses().find(s => s.value === value)?.severity ?? 'secondary';
    }

    /** Sentido de la llamada, con el icono que la representa. */
    getDirections() {
        return [
            { label: 'Entrante', value: 'In', icon: 'pi pi-arrow-down-left' },
            { label: 'Saliente', value: 'Out', icon: 'pi pi-arrow-up-right' },
            { label: 'Interna', value: 'Internal', icon: 'pi pi-arrows-h' },
        ];
    }

    getDirectionLabel(value?: string): string {
        return this.getDirections().find(d => d.value === value)?.label ?? (value ?? '');
    }

    getDirectionIcon(value?: string): string {
        return this.getDirections().find(d => d.value === value)?.icon ?? '';
    }

    getCallTypes() {
        return [
            { label: 'Operario a tramitador', value: 'worker-processor' },
            { label: 'Automática', value: 'automation' },
            { label: 'Interna', value: 'internal' },
            { label: 'Externa', value: 'external' },
        ];
    }

    /**
     * Icono (PrimeIcons) y color de cada tipo de evento de la cronología. El color es una variable
     * de la paleta del tema.
     */
    getTimelineKind(kind?: string): { icon: string, color: string } {
        const kinds: { [kind: string]: { icon: string, color: string } } = {
            'start': { icon: 'pi pi-phone', color: 'var(--primary-color)' },
            'queue-enter': { icon: 'pi pi-users', color: 'var(--blue-500)' },
            'ring-noanswer': { icon: 'pi pi-bell', color: 'var(--gray-400)' },
            'rejected': { icon: 'pi pi-times', color: 'var(--orange-500)' },
            'busy': { icon: 'pi pi-clock', color: 'var(--orange-500)' },
            'unavailable': { icon: 'pi pi-ban', color: 'var(--gray-500)' },
            'answered-elsewhere': { icon: 'pi pi-arrow-right-arrow-left', color: 'var(--gray-400)' },
            'caller-hangup': { icon: 'pi pi-bell-slash', color: 'var(--gray-400)' },
            'answered': { icon: 'pi pi-check', color: 'var(--green-500)' },
            'external-answered': { icon: 'pi pi-check', color: 'var(--green-500)' },
            'ivr': { icon: 'pi pi-list', color: 'var(--blue-400)' },
            'talk-end': { icon: 'pi pi-stop', color: 'var(--green-700)' },
            'transferred': { icon: 'pi pi-share-alt', color: 'var(--teal-500)' },
            'abandoned': { icon: 'pi pi-sign-out', color: 'var(--red-500)' },
            'timeout': { icon: 'pi pi-hourglass', color: 'var(--red-500)' },
            'queue-empty': { icon: 'pi pi-user-minus', color: 'var(--red-500)' },
            'exit-key': { icon: 'pi pi-sign-out', color: 'var(--orange-500)' },
            'line-busy': { icon: 'pi pi-minus-circle', color: 'var(--orange-500)' },
            'no-answer': { icon: 'pi pi-times-circle', color: 'var(--red-500)' },
            'end': { icon: 'pi pi-power-off', color: 'var(--gray-600)' },
        };
        return kinds[kind ?? ''] ?? { icon: 'pi pi-circle', color: 'var(--gray-400)' };
    }

    /** Cronología legible de la llamada (endpoint custom, no JSON:API). */
    getTimeline(id: string): Observable<CallTimelineEvent[]> {
        return this.http.get<CallTimelineEvent[]>(`${environment.api_gw}${this.url}/${id}/timeline`);
    }
}
